import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { router } from "./router.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { ToastContainer } from "./components/ui/ToastContainer.tsx";
import { TenantProvider } from "./stores/tenantStore.tsx";
import { UpgradeModal } from "./components/v2/UpgradeModal.tsx";
import { handlePlanLimitError } from "./hooks/usePlanGate.ts";
import { captureUtmParams } from "./hooks/useUtm.ts";
import { captureReferralCode } from "./hooks/useReferral.ts";
import "./styles/onboarding.css";
import "./styles/landing-variants.css";

// Capture UTM params and referral codes from URL on first load
captureUtmParams();
captureReferralCode();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      // If any mutation returns a plan-limit 402, show the upgrade modal.
      // The handler returns true if it consumed the error.
      handlePlanLimitError(error);
    },
  }),
});

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <TenantProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            <ToastContainer />
            <UpgradeModal />
          </QueryClientProvider>
        </TenantProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
